// src/components/calendar/CalendarLayout/CalendarLayout.jsx - Updated to Accept Navigation State
import React, { useEffect } from 'react';
import { cn } from '../../../utils/cn';

// Import shadcn navigation components
import CalendarHeader from '../navigation/CalendarHeader/CalendarHeader';
// TODO: CalendarFilters component needs to be recreated with 
// import CalendarFilters from '../navigation/CalendarFilters/CalendarFilters';

// Import the hooks (fallback only)
import { useCalendarNavigation } from '../../../hooks';

const CalendarLayout = ({
  // Calendar view components
  MonthViewComponent,
  WeekViewComponent,
  DayViewComponent,

  // Event panel component (optional)
  EventPanelComponent,

  // Props passed to calendar views
  onTimeSlotClick,
  onEventClick,
  onCreateEvent,

  // User permissions
  isAdmin = false,

  // Layout options
  showFilters = true,
  showCreateButton = true,
  compact = false,

  // Event panel state (if using external panel)
  showEventPanel = false,
  previewEvent = null,


  // ==========================================
  // NAVIGATION STATE (can be passed in)
  // ==========================================
  navigationState = null, // If provided, uses this instead of internal state

  // ✅ CHANGE 2: Accept formatted events from parent
  calendarEvents = [],      // Pre-formatted events with Date objects
  loading = false,          // Loading state from parent
  error = null,             // Error state from parent

  // creation mode props
  isInCreationMode = false,
  onCalendarClickDuringCreation,

  // NEW: Partner availability props
  availabilityDates,           // ← ADD THIS
  viewingPartnerAvailability,  // ← ADD THIS

  // Time range props
  timeRange,
  onTimeRangeChange,

  // Block mode props
  isBlockMode = false,
  persistedSlots = [],
  onSlotsChanged,

  // Selected event for detail panel
  selectedEventId = null,

  // Layout control
  renderHeader = true,  // When false, header is rendered by parent

  className = ''
}) => {
  // ==========================================
  // NAVIGATION STATE MANAGEMENT
  // ==========================================

  // Use passed navigation state OR create internal fallback
  const internalNavigation = useCalendarNavigation({
    initialView: 'timeGridWeek',
    initialDate: new Date(),
    updateURL: false // Don't update URL if using as fallback
  });

  // Use passed navigation state if provided, otherwise use internal
  const navigation = navigationState || internalNavigation;

  const {
    currentView,
    currentDate,
    currentTitle,
    setView,
    navigatePrevious,
    navigateNext,
    navigateToToday,
    getDateRange,
    handleKeyboardNavigation,
    // Filter state
    statusFilter,
    categoryFilter,
    customerFilter,
    setStatusFilter,
    setCategoryFilter,
    setCustomerFilter,
    clearFilters
  } = navigation;

  // ✅ CHANGE 3: REMOVE internal useEvents hook call
  // const {
  //   calendarEvents,
  //   loadCalendarEvents,
  //   loading,
  //   error
  // } = useEvents();

  // ✅ CHANGE 4: REMOVE internal event loading useEffect
  // useEffect(() => {
  //   const { start, end } = getDateRange();
  //   console.log('📅 CalendarLayout: Loading events for', { start, end, currentView });
  //   loadCalendarEvents(start, end);
  // }, [currentDate, currentView, loadCalendarEvents, getDateRange]);

  // Keyboard navigation (only if not using external navigation)
  useEffect(() => {
    if (!navigationState) { // Only add keyboard listener if we're managing navigation internally
      window.addEventListener('keydown', handleKeyboardNavigation);
      return () => window.removeEventListener('keydown', handleKeyboardNavigation);
    }
  }, [handleKeyboardNavigation, navigationState]);

  // ==========================================
  // EVENT FILTERING
  // ==========================================

  // Apply filters to events
  const filteredEvents = calendarEvents.filter(event => {
    if (statusFilter && event.status !== statusFilter) return false;
    if (categoryFilter && event.event_category !== categoryFilter) return false;
    if (customerFilter && event.customer?.id !== customerFilter) return false;
    return true;
  });

  // ✅ CHANGE 5: Add debug logging to verify events being passed to views
  console.log('📅 CalendarLayout: Events to render', {
    totalEvents: calendarEvents.length,
    filteredEvents: filteredEvents.length,
    sampleEvent: filteredEvents[0],
    hasStartDateObjects: filteredEvents.length > 0 && filteredEvents[0].start instanceof Date
  });

  // Get available filter options from current events
  const availableStatuses = [...new Set(calendarEvents.map(e => e.status).filter(Boolean))];
  const availableCategories = [...new Set(calendarEvents.map(e => e.event_category).filter(Boolean))];
  const availableCustomers = [...new Set(calendarEvents.map(e => e.customer).filter(Boolean))];

  // ==========================================
  // VIEW COMPONENT RENDERING
  // ==========================================

  // Get current view component
  const getCurrentViewComponent = () => {
    const commonProps = {
      currentDate,
      events: filteredEvents,
      previewEvent,
      onTimeSlotClick,
      onEventClick,
      isAdmin,
      showEventPanel,
      loading,
      error,
      isInCreationMode,
      onCalendarClickDuringCreation,
      // NEW: Pass availability props to all view components
      availabilityDates,           // ← ADD THIS
      viewingPartnerAvailability,  // ← ADD THIS
      // NEW: Pass time range to views that need it
      timeRange,
      // Block mode props
      isBlockMode,
      persistedSlots,
      onSlotsChanged,

      // Selected event for detail panel
      selectedEventId
    };

    console.log('📅 CalendarLayout: Rendering view', currentView, 'with', filteredEvents.length, 'events');

    switch (currentView) {
      case 'dayGridMonth':
        return MonthViewComponent ? <MonthViewComponent {...commonProps} /> : (
          <div className="flex items-center justify-center h-64 text-slate-400">
            Month view component not provided
          </div>
        );
      case 'timeGridWeek':
        return WeekViewComponent ? <WeekViewComponent {...commonProps} /> : (
          <div className="flex items-center justify-center h-64 text-slate-400">
            Week view component not provided
          </div>
        );
      case 'timeGridDay':
        return DayViewComponent ? <DayViewComponent {...commonProps} /> : (
          <div className="flex items-center justify-center h-64 text-slate-400">
            Day view component not provided
          </div>
        );
      default:
        return WeekViewComponent ? <WeekViewComponent {...commonProps} /> : (
          <div className="flex items-center justify-center h-64 text-slate-400">
            Default view component not provided
          </div>
        );
    }
  };

  // ==========================================
  // COMPACT LAYOUT
  // ==========================================

  if (compact) {
    // Compact layout for mobile/small screens
    return (
      <div className={cn("flex flex-col h-full space-y-4", className)}>
        {/* Compact header - conditionally rendered */}
        {renderHeader && (
          <CalendarHeader
            currentView={currentView}
            currentTitle={currentTitle}
            onViewChange={setView}
            onPrevious={navigatePrevious}
            onNext={navigateNext}
            onToday={navigateToToday}
            onCreateEvent={onCreateEvent}
            showCreateButton={showCreateButton}
            isAdmin={isAdmin}
            loading={loading}
            compact={true}
            currentTimeRange={timeRange}
            onTimeRangeChange={onTimeRangeChange}
          />
        )}

        {/* Compact filters - TODO: Re-implement with  */}
        {/* {showFilters && (
          <div className="flex items-center justify-between">
            <CalendarFilters
              statusFilter={statusFilter}
              categoryFilter={categoryFilter}
              customerFilter={customerFilter}
              onStatusFilterChange={setStatusFilter}
              onCategoryFilterChange={setCategoryFilter}
              onCustomerFilterChange={setCustomerFilter}
              onClearFilters={clearFilters}
              availableStatuses={availableStatuses}
              availableCategories={availableCategories}
              availableCustomers={availableCustomers}
              compact={true}
            />
          </div>
        )} */}

        {/* Calendar view */}
        <div className="h-[600px] overflow-hidden">
          {getCurrentViewComponent()}
        </div>

        {/* Event panel (if provided and open) */}
        {EventPanelComponent && showEventPanel && (
          <div className="h-1/2">
            <EventPanelComponent />
          </div>
        )}
      </div>
    );
  }

  // ==========================================
  // FULL DESKTOP LAYOUT
  // ==========================================

  return (
    <div className={cn("flex flex-col h-full", renderHeader && "space-y-6", className)}>
      {/* Main header - conditionally rendered */}
      {renderHeader && (
        <CalendarHeader
          currentView={currentView}
          currentTitle={currentTitle}
          onViewChange={setView}
          onPrevious={navigatePrevious}
          onNext={navigateNext}
          onToday={navigateToToday}
          onCreateEvent={onCreateEvent}
          showCreateButton={showCreateButton}
          isAdmin={isAdmin}
          loading={loading}
          compact={false}
          currentTimeRange={timeRange}
          onTimeRangeChange={onTimeRangeChange}
        />
      )}

      {/* Content area */}
      <div className="flex-1 flex gap-6 overflow-hidden">
        {/* Sidebar with filters - TODO: Re-implement with  */}
        {/* {showFilters && (
          <div className="w-64 flex-shrink-0">
            <CalendarFilters
              statusFilter={statusFilter}
              categoryFilter={categoryFilter}
              customerFilter={customerFilter}
              onStatusFilterChange={setStatusFilter}
              onCategoryFilterChange={setCategoryFilter}
              onCustomerFilterChange={setCustomerFilter}
              onClearFilters={clearFilters}
              availableStatuses={availableStatuses}
              availableCategories={availableCategories}
              availableCustomers={availableCustomers}
              compact={false}
            />
          </div>
        )} */}

        {/* Calendar view */}
        <div className="flex-1 min-h-[700px] max-h-[700px] overflow-hidden">
          {getCurrentViewComponent()}
        </div>
      </div>

      {/* Event panel (if provided and open) */}
      {EventPanelComponent && showEventPanel && (
        <div className="fixed right-0 top-0 h-full w-96 z-50">
          <EventPanelComponent />
        </div>
      )}
    </div>
  );
};

export default CalendarLayout;